<template>
  <v-container grid-list-lg fluid pa-0>
    <v-container v-if="list.length > 0 || languageId != ''" pb-0 fluid>
      <v-row align="baseline">
        <v-switch
          v-model="showImages"
          class="px-3"
          label="Show Images"
          @change="onShowImagesChange"
        ></v-switch>
        <v-select
          v-model="columns"
          label="No. of Columns"
          :items="availableColumns"
          :outlined="outlinedInputs"
          class="my-0 mr-2"
        ></v-select>
        <v-select
          v-model="selectedContentName"
          label="Filter by Name"
          :items="contentNames"
          :outlined="outlinedInputs"
          class="my-0 mr-2"
          clearable
        ></v-select>
        <languages-list-select
          v-model="languageId"
          :outlined="outlinedInputs"
          label="Filter by Language"
          @input="onLangChange"
          clearable
        ></languages-list-select>
        <v-spacer></v-spacer>
        <v-btn @click="onNewContent" color="primary" text :disabled="viewOnly">
          <v-icon left>mdi-plus-circle</v-icon>New Content
        </v-btn>
      </v-row>
    </v-container>
    <add-btn
      v-else-if="!$apollo.loading"
      label="Add New Content"
      @click="onNewContent"
      :icon="'mdi-image'"
    ></add-btn>
    <v-row justify="center" v-show="$apollo.loading">
      <v-progress-circular
        indeterminate
        :size="50"
        color="primary"
      ></v-progress-circular>
    </v-row>
    <v-layout row wrap>
      <v-flex v-for="content in list" :key="content.id" :class="listColumns">
        <content-item
          :key="content.id"
          :value="content"
          :show-image="showImages"
          @onEdit="onContentEdit"
          @onViewImage="onViewImage"
          @onDelete="onContentDelete"
          :view-only="viewOnly"
        ></content-item>
      </v-flex>
    </v-layout>
    <content-edit-dialog
      :show="showEditor"
      :value="editedContent"
      :dialog-title="dialogTitle"
      @close="onEditorClose"
      @contentSave="onContentSave"
    ></content-edit-dialog>
    <confirmation-dialog
      :show-dialog="showConfirmDialog"
      @onConfirm="onContentRemoveConfirm"
      @onCloseDialog="onContentRemoveCancel"
      :item="contentToRemove"
    ></confirmation-dialog>

    <div v-if="debug">contents: {{ ownerName }}</div>
  </v-container>
</template>
<script>
import ContentItem from "./ContentItem";
import gql from "graphql-tag";
import ContentEditDialog from "./ContentEditDialog";
import ConfirmationDialog from "../../../gt/shared/ConfirmationDialog";
import LanguagesListSelect from "../../shared/LanguageListSelect";
import AddBtn from "../../shared/AddBtn";
import { uniqueByProp, removeGQLTypename } from "../../../../helpers/index";

const CONTENTS_QUERY = gql`
  query ($filter: ContentFilterType) {
    contents(filter: $filter) {
      edges {
        node {
          id
          name
          description
          imgURL
          languageId
          owner
          language {
            id
            code
          }
          isDefault
          forWeb
          forReports
          forQuotation
          forConfirmation
          forVoucher
          forOther
          forApp
          sequence
          images {
            edges {
              node {
                id
                name
                url
              }
            }
          }
        }
      }
    }
  }
`;
export const CONTENT_DELETE_MUTATION = gql`
  mutation ($contentId: ID!) {
    deleteContent(id: $contentId) {
      id
    }
  }
`;
export default {
  name: "contents",
  props: {
    ownerId: {
      type: String,
      required: true,
    },
    ownerName: {
      type: String,
      default: () => "",
    },
    dialogTitle: {
      type: String,
    },
    filter: {
      type: Object,
      default: () => ({}),
    },
    setDefaultLang: {
      type: Boolean,
      default: () => true,
    },
    viewOnly: {
      type: Boolean,
      default: () => false,
    },
  },
  components: {
    ContentItem,
    ContentEditDialog,
    ConfirmationDialog,
    AddBtn,
    LanguagesListSelect,
  },
  data() {
    return {
      debug: false,
      columns: 4,
      availableColumns: [2, 3, 4, 6, 12],
      selectedContentName: "",
      showEditor: false,
      showImages: this.$store.getters.localSettings(
        `${this.ownerName}-contents-settings`,
        false
      ),
      showConfirmDialog: false,
      editedContent: null,
      contentToRemove: null,
      skipLoad: false,
      newContent: {
        ownerId: this.ownerId,
        id: "",
        name: "New Content",
        owner: this.ownerName,
      },
      languageId:
        this.setDefaultLang && this.$store.getters.userConfig
          ? this.$store.getters.localSettings(
              `${this.ownerName}-contents-language`,
              this.$store.getters.userConfig.defaultLanguageId
            )
          : "",
    };
  },
  computed: {
    contentNames() {
      return this.contents && this.contents.edges
        ? uniqueByProp(this.contents.edges.map(removeGQLTypename), "name").map(
            (c) => c.name
          )
        : [];
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    listColumns() {
      return `xs12 sm3 lg${12 / this.columns}`;
    },
    list() {
      return this.contents && this.contents.edges
        ? this.selectedContentName
          ? this.contents.edges
              .map((c) => ({ ...c.node }))
              .filter((c) => c.name == this.selectedContentName)
          : this.contents.edges.map((c) => ({ ...c.node }))
        : [];
    },
    settingsName() {
      return `${this.ownerName}-contents-settings`;
    },
  },
  watch: {
    ownerId(newOwner) {
      this.skipLoad = false;
    },
    ownerName(newOwner) {
      this.skipLoad = false;
    },
  },
  methods: {
    onShowImagesChange(event) {
      this.$store.dispatch("updateLocalSettings", {
        [this.settingsName]: event,
      });
    },
    onContentRemoveCancel() {
      this.contentToRemove = null;
      this.showConfirmDialog = false;
    },
    onContentRemoveConfirm(payload) {
      this.showConfirmDialog = false;
      this.removeContent();
    },
    removeContent() {
      this.$apollo
        .mutate({
          mutation: CONTENT_DELETE_MUTATION,
          variables: {
            contentId: this.contentToRemove.id,
          },
        })
        .then((res) => {
          this.skipLoad = false;
          this.$store.dispatch("showAppSnackbar", { msg: "Content removed." });
        })
        .catch((err) => {
          console.error(err);
        });
    },
    onContentEdit(payload) {
      this.editedContent = { ...payload, ownerId: this.ownerId };
      this.showEditor = true;
    },
    onContentDelete(payload) {
      this.contentToRemove = { ...payload };
      this.showConfirmDialog = true;
    },
    onViewImage(payload) {
      console.log("on view image", payload);
    },
    onEditorClose() {
      this.showEditor = false;
    },
    onContentSave(payload) {
      if (["saveAndClose", "saveAsNew"].indexOf(payload) > -1) {
        this.showEditor = false;
      }
      this.editedContent = { ...this.newContent, ownerId: this.ownerId }; //https://tracker.globetrack.ie/issues/6161
      this.$store.dispatch("showAppSnackbar", { msg: "Content saved." });
      this.skipLoad = false;
    },
    onNewContent(event) {
      this.editedContent = {
        ownerId: this.ownerId,
        id: "",
        name: "New Content",
        languageId: this.$store.getters.userConfig.defaultLanguageId,
      };
      if (this.ownerName != "") {
        this.editedContent.owner = this.ownerName;
      }
      this.showEditor = true;
    },
    onLangChange() {
      this.$store.dispatch("updateLocalSettings", {
        [`${this.ownerName}-contents-language`]: this.languageId,
      });
      this.skipLoad = false;
    },
  },
  apollo: {
    contents: {
      query: CONTENTS_QUERY,
      variables() {
        const filter = { ...this.filter };
        if (this.ownerName != "") {
          filter.owner = this.ownerName;
        }
        if (this.ownerId != 0) {
          filter.owner_id = this.ownerId;
        }
        if (this.languageId != "") {
          filter.language_id = this.languageId;
        }
        //console.log("owner props", this.ownerName, this.ownerId);
        return {
          filter,
        };
      },
      skip() {
        return this.skipLoad;
      },
      result() {
        this.skipLoad = true;
      },
      fetchPolicy: "network-only",
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/shared/contents/Contents.vue